import { useState } from 'react';
import { useAdmin } from '@snipit/core/hooks/useAdmin';
import { Shield, ShieldAlert, CheckCircle, Activity, Users, ZoomIn, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import OptimizedImage from '@snipit/ui/components/OptimizedImage';

export default function AdminView({ isEmbedded = false }) {
  const { users, pendingGuides, logs, userLogs, loading, isAdmin, toggleBlockUser, adminValidateGuide } = useAdmin();
  const [activeTab, setActiveTab] = useState('users');
  const { t } = useTranslation();

  if (loading) {
    return <div className="p-8 text-white">{t('Loading Admin Dashboard...')}</div>;
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <ShieldAlert size={64} className="text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">{t('Access Denied')}</h2>
        <p className="text-gray-400">{t('You do not have permission to view this page.')}</p>
      </div>
    );
  }

  return (
    <div className={isEmbedded ? "flex flex-col h-full overflow-y-auto" : "w-full h-full bg-[#121212] text-gray-300 flex flex-col p-6 lg:p-8 overflow-y-auto"}>
      {!isEmbedded && (
        <div className="flex items-center gap-3 mb-8 shrink-0">
          <Shield className="text-indigo-500" size={32} />
          <h1 className="text-3xl font-bold text-white tracking-tight">{t('Admin Dashboard')}</h1>
        </div>
      )}

      <div className="flex gap-4 mb-6 border-b border-gray-800 pb-2 shrink-0 overflow-x-auto">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap ${
            activeTab === 'users' ? 'text-indigo-400' : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <Users size={16} /> User Moderation
          {activeTab === 'users' && (
            <span className="absolute -bottom-2.25 left-0 right-0 h-0.5 bg-indigo-500 rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('guides')}
          className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap ${
            activeTab === 'guides' ? 'text-indigo-400' : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <CheckCircle size={16} /> Pending Guides
          {pendingGuides.length > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{pendingGuides.length}</span>}
          {activeTab === 'guides' && (
            <span className="absolute -bottom-2.25 left-0 right-0 h-0.5 bg-indigo-500 rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap ${
            activeTab === 'logs' ? 'text-indigo-400' : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <Activity size={16} /> System Logs
          {activeTab === 'logs' && (
            <span className="absolute -bottom-2.25 left-0 right-0 h-0.5 bg-indigo-500 rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('userLogs')}
          className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors relative whitespace-nowrap ${
            activeTab === 'userLogs' ? 'text-indigo-400' : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <Activity size={16} /> User Activity
          {activeTab === 'userLogs' && (
            <span className="absolute -bottom-2.25 left-0 right-0 h-0.5 bg-indigo-500 rounded-t-full" />
          )}
        </button>
      </div>

      <div className="grow">
        {activeTab === 'users' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white mb-4">{t('User Directory & Moderation')}</h2>
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {users.map(user => (
                <div key={user.id} className="bg-[#1a1a1a] border border-gray-800 p-4 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {user.photoURL ? (
                      <OptimizedImage src={user.photoURL} alt="avatar" className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                        <span className="font-bold">{user.displayName?.charAt(0) || '?'}</span>
                      </div>
                    )}
                    <div>
                      <p className="font-bold text-white text-sm">{user.displayName}</p>
                      <p className="text-xs text-gray-400">{user.email}</p>
                      <p className="text-[10px] text-emerald-400 font-bold mt-1">{user.devItCreds || 0} Creds</p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleBlockUser(user.id, user.isBlocked, user.displayName)}
                    className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
                      user.isBlocked 
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        : 'bg-red-900/30 text-red-400 hover:bg-red-900/50 border border-red-900/50'
                    }`}
                  >
                    {user.isBlocked ? 'Unblock' : 'Block'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'guides' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white mb-4">{t('Pending Guides')}</h2>
            {pendingGuides.length === 0 ? (
              <p className="text-gray-500 italic">{t('No pending guides to review.')}</p>
            ) : (
              <div className="space-y-4">
                {pendingGuides.map(guide => (
                  <div key={guide.id} className="bg-[#1a1a1a] border border-gray-800 p-5 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-white text-sm">{guide.authorName}</span>
                        <span className="text-xs text-gray-500">{new Date(guide.createdAt).toLocaleString()}</span>
                      </div>
                      <span className="bg-indigo-900/40 text-indigo-400 border border-indigo-900/60 text-[10px] px-2 py-0.5 rounded font-bold">{t('Guide / Tip')}</span>
                    </div>
                    <p className="text-sm text-gray-300 whitespace-pre-wrap mb-4">{guide.content}</p>
                    {/* Render Attachment */}
                    {guide.attachmentUrl && (
                      <div className="mb-4">
                        {guide.attachmentType?.startsWith('image/') ? (
                          <a href={guide.attachmentUrl} target="_blank" rel="noopener noreferrer" className="inline-block relative group mt-1">
                            <OptimizedImage src={guide.attachmentUrl} alt="attachment" className="max-h-28 rounded border border-gray-700 group-hover:opacity-75 transition object-cover" />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none bg-black/20 rounded">
                              <ZoomIn size={24} className="text-white drop-shadow-md" />
                            </div>
                          </a>
                        ) : (
                          <a href={guide.attachmentUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 bg-[#1a1a1a] border border-gray-700 rounded-lg hover:bg-gray-800 transition w-fit">
                            <FileText size={24} className="text-indigo-400" />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-white">{guide.attachmentName || 'Document Attachment'}</span>
                              <span className="text-[10px] text-gray-500">{t('Click to view/download')}</span>
                            </div>
                          </a>
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => adminValidateGuide(guide.id, guide.userId)}
                      className="bg-emerald-900/30 text-emerald-400 border border-emerald-900/50 px-3 py-1.5 rounded text-xs font-bold hover:bg-emerald-900/50 transition flex items-center gap-2"
                    >
                      <CheckCircle size={14} /> Validate & Award 10 Creds
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white mb-4">{t('Admin Audit Logs')}</h2>
            {logs.length === 0 ? (
              <p className="text-gray-500 italic">{t('No admin actions have been logged yet.')}</p>
            ) : (
              <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-left text-sm text-gray-300">
                  <thead className="bg-[#222] text-gray-400 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-3">{t('Timestamp')}</th>
                      <th className="px-4 py-3">{t('Admin')}</th>
                      <th className="px-4 py-3">{t('Action')}</th>
                      <th className="px-4 py-3">{t('Details')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {logs.map(log => (
                      <tr key={log.id} className="hover:bg-[#222] transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">{new Date(log.timestamp).toLocaleString()}</td>
                        <td className="px-4 py-3 font-medium text-white">{log.adminName}</td>
                        <td className="px-4 py-3">
                          <span className="bg-gray-800 text-gray-300 px-2 py-0.5 rounded text-[10px] font-bold">{log.action}</span>
                        </td>
                        <td className="px-4 py-3">{log.details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'userLogs' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white mb-4">{t('User Activity Logs')}</h2>
            {userLogs.length === 0 ? (
              <p className="text-gray-500 italic">{t('No user activities have been logged yet.')}</p>
            ) : (
              <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-left text-sm text-gray-300">
                  <thead className="bg-[#222] text-gray-400 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-3">{t('Timestamp')}</th>
                      <th className="px-4 py-3">{t('User')}</th>
                      <th className="px-4 py-3">{t('Action')}</th>
                      <th className="px-4 py-3">{t('Details')}</th>
                      <th className="px-4 py-3">{t('Creds')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {userLogs.map(log => (
                      <tr key={log.id} className="hover:bg-[#222] transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                          {log.timestamp ? (log.timestamp.toDate ? log.timestamp.toDate().toLocaleString() : new Date(log.timestamp).toLocaleString()) : 'Pending...'}
                        </td>
                        <td className="px-4 py-3 font-medium text-white">{log.userName}</td>
                        <td className="px-4 py-3">
                          <span className="bg-gray-800 text-gray-300 px-2 py-0.5 rounded text-[10px] font-bold">{log.action}</span>
                        </td>
                        <td className="px-4 py-3">{log.details}</td>
                        <td className="px-4 py-3 font-medium text-emerald-400">{log.credsAwarded ? `+${log.credsAwarded}` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
